import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RateLimitModal = ({ isOpen, onClose, resetTime, dailyQuota = 5 }) => {
  const [timeUntilReset, setTimeUntilReset] = useState(null);

  useEffect(() => {
    if (!isOpen || !resetTime) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const reset = new Date(resetTime);
      const diff = reset - now;

      if (diff <= 0) {
        setTimeUntilReset({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeUntilReset({ hours, minutes, seconds });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [isOpen, resetTime]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg max-w-md w-full border border-border shadow-lg">
        {/* Header */}
        <div className="bg-gradient-to-r from-destructive/10 to-destructive/5 p-6 border-b border-border rounded-t-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Icon
                name="AlertCircle"
                size={28}
                color="var(--color-destructive)"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">
                Daily Limit Reached
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                You've used all {dailyQuota} of your analyses for today
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Countdown Timer */}
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-3 text-center font-medium">
              Your quota resets in
            </p>
            {timeUntilReset && (
              <div className="flex justify-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {String(timeUntilReset.hours).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground">hours</div>
                </div>
                <div className="text-2xl font-bold text-muted-foreground">:</div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {String(timeUntilReset.minutes).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground">minutes</div>
                </div>
                <div className="text-2xl font-bold text-muted-foreground">:</div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">
                    {String(timeUntilReset.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground">seconds</div>
                </div>
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">
              In the meantime, you can:
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <Icon
                  name="CheckCircle2"
                  size={16}
                  color="var(--color-success)"
                  className="flex-shrink-0 mt-0.5"
                />
                <span>
                  Complete your <strong>portfolio</strong> to unlock bonus analyses
                </span>
              </li>
              <li className="flex gap-2">
                <Icon
                  name="CheckCircle2"
                  size={16}
                  color="var(--color-success)"
                  className="flex-shrink-0 mt-0.5"
                />
                <span>
                  Review your <strong>past analyses</strong> and improve your applications
                </span>
              </li>
              <li className="flex gap-2">
                <Icon
                  name="CheckCircle2"
                  size={16}
                  color="var(--color-success)"
                  className="flex-shrink-0 mt-0.5"
                />
                <span>
                  Explore more <strong>universities</strong> to prepare for tomorrow
                </span>
              </li>
            </ul>
          </div>

          {/* Info Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-foreground">
              <strong>Bonus Analyses:</strong> Complete 100% of your portfolio to earn 2 bonus analyses per week!
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 rounded-b-lg bg-muted/30 flex gap-3">
          <Button
            variant="primary"
            onClick={onClose}
            className="flex-1"
          >
            Close
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              window.location.href = '/portfolio-builder';
            }}
            className="flex-1"
          >
            Complete Portfolio
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RateLimitModal;
